/**
 * What brings people here (A8 of documentation/pr-plans/site-insights-answers-root-1.md), by search intent (D148 of
 * documentation/pr-plans/insights-simplify-plan.md, Asana P44): a stack whose top edge is the site's search clicks,
 * its page views twin, and a table under it that follows the level on screen.
 *
 * Three levels, each the server's answer (TopicsCardBuilder): the bands (each search intent, Not sorted, Branded
 * queries, Not relevant and Hidden by Google), one band's topics, and one topic's searches, drawn alone. A band's
 * or a topic's name in the table opens it (the chart host reports a click's day, not its band); the breadcrumb and
 * the browser's Back walk back up. Each level has its own address (?intent=, ?topic=), written as a new history entry, so a reload or a
 * copied address reopens it and Back undoes one step (go()). A link that names a topic (?topic=, D76) opens it in
 * the band its search intent puts it in, which the answer names and the address then takes in place.
 *
 * The table is the legend: each row carries its band's colour, and an eye that takes it off the chart and puts it
 * back (toggleHidden()). The server composes the stack without it, so a row summed into Other topics leaves that
 * sum too, and the tooltip's total then says how much of the whole the chart shows ("48 of 63"), so the picture
 * never reads as complete when it is not. Inside a band the table lists every topic, TABLE_ROWS a page.
 *
 * The card answers on its own browser events, never on site-analytics-data (D31): the page's trend draws every
 * series it does not know as a line of its own. `site-analytics-topics` carries the card's figures,
 * `site-analytics-topic-queries` the opened group's searches and `site-analytics-topic-pages` one opened search's
 * pages. Every figure arrives formatted: nothing here formats or adds up a number.
 *
 * The stack is drawn through the page's chart contract (charts/site-analytics-chart-contract.js), stacked as A7
 * stacks the branded card, with the page's own tooltip, on a chart host of the card's own: pastel areas (STACK_FILL)
 * under edges in each band's full colour, which keep a chart mark's 3:1 (P44). The chart modules are imported
 * dynamically, as site-analytics-report.js imports them: this module is reachable from app.js, the one Vite entry
 * on every layout.
 *
 * The searches level lists the opened group's searches (D18), as one flat list of rows under the topic's row:
 * each search, its pages once opened, and what is said in their place (childRows()), in a window ten searches
 * high that scrolls for the rest, under a search box (searchGroup(); Ola's QA, 9 Oct; on every list, however short,
 * since D173). A search of the AI's Unsafe searches topic reads "Unsafe search" wherever the card shows or says it,
 * until the reader presses the list's one Show queries over it (queryName(), toggleReveal(); D47, S29, D204).
 *
 * One band's topics and one topic's searches sort by any column the table fills, one press on its header
 * (<x-insights.sortable-header>, D173): largest first, a name A to Z and a position top of Google first, a second
 * press the other way, as the activity tables sort. Both arrive in the server's order, clicks most first, which
 * their Clicks header says until another is pressed. The rows are put in order here, from the figures each row
 * carries unformatted (`sort`): the topics' pager and the searches' window follow the order. A search the browser
 * cannot answer from the rows it holds asks the server in the order on screen, so its first matches are the
 * first in that order (reorderSearches()). The order holds for the page's life, level to level. One band's topics
 * also narrow by a box over their table, by name, here, as the searches' box narrows a list it holds whole
 * (searchTopics()): the box, the order and the pager compose, and the chart above keeps every topic.
 *
 * Prefix: none. The card's scope is its own, inside the page's SiteAnalyticsReport scope, so its names cannot
 * collide with the report's sar_ ones.
 */

import { NEW_TOPIC_COLORS, TOPIC_BAND_COLORS } from '../charts/palette';
import motion from '../motion-tokens.json' with { type: 'json' };
import { HIGHLIGHT_MS } from './insights-activity-table';
import { INTENT_PARAM, STATS_VIEW, TOPIC_PARAM, VIEW_PARAM, topicId } from './insights-links';
import { ASCENDING, DESCENDING, ariaSort, nextSort, sortArrow, sortRows, sortSentence } from './sortable-header';

/** The event <x-charts.superset-chart> listens on (resources/js/alpine/superset-chart.js). */
const HOST_EVENT = 'superset-chart-data';

const CLICKS = 'clicks';

const PAGE_VIEWS = 'page_views';

/** TopicsCardBuilder's levels. */
export const LEVEL_BANDS = 'bands';

export const LEVEL_TOPICS = 'topics';

export const LEVEL_SEARCHES = 'searches';

/** How long the card must hold still on screen before a link's landing scrolls to it, and the longest wait. */
const SETTLE_STILL_MS = 400;

const SETTLE_MAX_MS = 3000;

/** What the reader does that means they have taken over the page: a landing then moves nothing. */
const READER_MOVES = ['wheel', 'touchmove', 'keydown', 'pointerdown'];

/** What the server answers when a load moved nothing the card shows. */
const KEPT = 'kept';

const READY = 'ready';

const REFUSED = 'refused';

/** A row's own states, as its answer names them, and while it is out. */
const LOADING = 'loading';

/**
 * The source every band is labelled under. The chart contract qualifies two bands of one name by it,
 * and the builder has already made every band name unique, so it is never printed.
 */
const SOURCE = 'topics';

/** How many rows of one band's topics the table lists a page (D98's ten a page). */
export const TABLE_ROWS = 10;

/**
 * An opened topic's list shows this many searches and scrolls for the rest (Ola's QA, 9 Oct). The Blade's
 * x-row-window says the same number, which SiteAnalyticsTopicSearchTest holds the two to.
 */
export const SEARCH_WINDOW_ROWS = 10;

/** How long the search box waits after a key before it asks the server, for a topic not held whole. */
const SEARCH_ASK_MS = 300;

/** Long enough that a reader typing a word hears one count, not one per letter (the activity table's). */
const COUNT_ANNOUNCE_MS = 500;

/** How long an eye waits after a press for another, so a few quick presses are one load. */
const HIDE_DEBOUNCE_MS = 250;

/** The two tables whose headers sort (D173). The bands keep their fixed order. */
export const SORT_TOPICS = 'topics';

export const SORT_SEARCHES = 'searches';

/**
 * Each table's columns, by the key its header passes, with the kind its first press and its words follow: a
 * name (the row's name, a search's text), a position, or a figure, which a row carries under `sort`. A search
 * has no page views or key events, so those headers of the searches table do not sort. Only a search has an
 * average position, a column of its table alone.
 */
const SORT_COLUMNS = {
    [SORT_TOPICS]: { name: 'text', clicks: 'number', change: 'number', impressions: 'number', pageViews: 'number', keyEvents: 'number' },
    [SORT_SEARCHES]: { query: 'text', clicks: 'number', impressions: 'number', position: 'position' },
};

/** The order both tables arrive in: TopicsCardBuilder's topics and TopicSql::queries()' searches, clicks most first. */
const SERVER_SORT = { key: 'clicks', direction: DESCENDING };

/** An order as an answer echoes it (TopicSql::queryOrder(), null for the server's own), as one string to compare. */
function orderToken(order) {
    return order ? `${order.column}:${order.direction}` : '';
}

/**
 * The stack (P44): pastel areas under 1.5px edges in each band's full colour. The edge carries the band's
 * contrast with the page (3:1 for a chart mark, WCAG 1.4.11), so the fill can be light: .24 of the colour,
 * where S47's .42 read bright across the large Hidden by Google and Branded queries bands.
 */
export const STACK_FILL = 0.24;

export const STACK_LINE_WIDTH = 1.5;

/**
 * How far an opened row's searches rise as they fade in (the v3 spec's 6px). How long they take and on
 * which curve are the motion tokens' `show` and `ease`, read from there and never spelled here (D74).
 */
const FADE_UP_PX = 6;

/**
 * A colour from TopicsCardBuilder as the chart draws it: a hex as sent (a search intent's, FunnelStage::PALETTE,
 * whose one home is PHP), "topics.<rank>" and "new.<index>" from the band palette (resources/js/charts/palette.js),
 * TOPIC_BAND_COLORS' own key for any other band, and null for none.
 */
export function bandColor(key) {
    if (typeof key !== 'string') return null;
    if (/^#[0-9a-f]{6}$/i.test(key)) return key;

    const [palette, index] = key.split('.');

    if (palette === 'topics') return TOPIC_BAND_COLORS.topics[Number(index)] ?? null;
    if (palette === 'new') return NEW_TOPIC_COLORS[Number(index)] ?? null;

    return typeof TOPIC_BAND_COLORS[key] === 'string' ? TOPIC_BAND_COLORS[key] : null;
}

/** Whether the reader asked for reduced motion, which turns every animation off (D74). */
function prefersReducedMotion() {
    return typeof window?.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * The level an address names: ?intent= (a band, which the server checks) and ?topic= (a group id: a topic's, or
 * Other queries' 0). Read here rather than handed in by the server: Stats mounts lazily, so the request that
 * renders the card is Livewire's own and carries no address.
 */
export function viewFromAddress(search) {
    const params = new URLSearchParams(search ?? '');
    const intent = params.get(INTENT_PARAM);

    return { intent: typeof intent === 'string' && intent !== '' ? intent : null, topic: topicId(params.get(TOPIC_PARAM)) };
}

/** The topic the address asks this card to open as it starts (?topic=<id>), or null. */
export function focusFromAddress(search) {
    return viewFromAddress(search).topic;
}

/** Whether the reader asked for reduced motion, under which a landing's scroll jumps rather than glides. */
function isMotionReduced() {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
}

/**
 * One scroll that puts the card on screen below the pinned bars (their scroll margin), its top first, so a
 * link's topic is seen drawn in the chart above its searches.
 */
export function scrollToLanding(card, row, viewportHeight, isReduced) {
    if (!row) return;

    const margin = card ? Number.parseFloat(window.getComputedStyle?.(card)?.scrollMarginTop ?? '') || 0 : 0;
    const fits = card !== null && row.getBoundingClientRect().bottom - card.getBoundingClientRect().top <= viewportHeight - margin;

    (fits ? card : row).scrollIntoView({ block: 'start', behavior: isReduced ? 'auto' : 'smooth' });
}

/** The page's address, or null outside a browser (the Node harness, a preview). */
function currentAddress() {
    return typeof window !== 'undefined' && window.location?.href ? new URL(window.location.href) : null;
}

/**
 * Calls `settled` once the element has held still on screen for SETTLE_STILL_MS, or after
 * SETTLE_MAX_MS. The topics card's answer can arrive while the cards above it are still drawing and
 * growing (measured: the card slid 200px down over the second after its answer), so a scroll made at
 * once lands where the page then pushes it from. If the reader scrolls, presses a key or a pointer
 * meanwhile they have taken over, and `cancelled` runs instead, so nothing yanks the page from them.
 * Outside a browser (the Node harness) it settles at once.
 */
function whenSettled(element, settled, cancelled) {
    if (typeof requestAnimationFrame !== 'function' || typeof element?.getBoundingClientRect !== 'function') {
        setTimeout(settled);

        return;
    }

    let isTakenOver = false;
    const takeOver = () => {
        isTakenOver = true;
    };
    const started = performance.now();
    let stillSince = started;
    let lastTop = element.getBoundingClientRect().top;

    READER_MOVES.forEach((type) => window.addEventListener(type, takeOver, { capture: true, passive: true }));

    const finish = (callback) => {
        READER_MOVES.forEach((type) => window.removeEventListener(type, takeOver, { capture: true }));
        callback();
    };

    const watch = (now) => {
        if (isTakenOver) return finish(cancelled);

        const top = element.getBoundingClientRect().top;

        if (Math.abs(top - lastTop) >= 1) {
            lastTop = top;
            stillSince = now;
        }

        if (now - stillSince >= SETTLE_STILL_MS || now - started >= SETTLE_MAX_MS) return finish(settled);

        requestAnimationFrame(watch);
    };

    requestAnimationFrame(watch);
}

/**
 * Whether focus is free to move: on nothing, or on an element only a script focuses (tabindex="-1"),
 * never on a control the reader moved to while the card loaded.
 */
function isFocusFree() {
    const active = typeof document === 'undefined' ? null : document.activeElement;

    return !active || active === document.body || active.getAttribute?.('tabindex') === '-1';
}

/**
 * One stack from the answer as the chart contract reads a model (mergeTrendSeries()'s shape): the
 * bands bottom to top on one axis, and the total, which no legend offers and no contract draws, for the
 * tooltip to print under them.
 */
export function stackModel(stack) {
    return {
        interval: stack.interval,
        dates: stack.dates,
        series: stack.bands.map((band) => ({
            key: band.key,
            measureKey: band.key,
            source: SOURCE,
            name: band.name,
            unit: stack.unit,
            axis: 'left',
            color: bandColor(band.color),
            values: band.values,
            formatted: band.formatted,
        })),
        total: {
            key: 'total',
            measureKey: 'total',
            source: SOURCE,
            name: stack.total.name,
            unit: stack.unit,
            axis: 'left',
            colorIndex: 0,
            values: stack.total.values,
            formatted: stack.total.formatted,
        },
    };
}

/** Two levels the same: the same band and the same topic. */
function isSameView(a, b) {
    return (a?.intent ?? null) === (b?.intent ?? null) && (a?.topic ?? null) === (b?.topic ?? null);
}

/**
 * @param {object} config
 * @param {string} config.id       The card's element id prefix; every row id starts with it.
 * @param {string} config.hostId   Its chart host's id.
 * @param {object} config.strings  The card's words, from the topics_card block.
 * @param {object} config.formats  Unit -> number-format descriptor (SiteAnalyticsFormatter::chartFormats()).
 */
export default ({ id = 'site-analytics-topics', hostId = 'sa-topics-stack', strings = {}, formats = {} } = {}) => {
    // The last detail the host was sent, so a redraw of the same stack does not re-mount the chart
    // and replay its entry animation.
    let sent = null;

    // Each draw's own number: the chart modules arrive asynchronously, and a draw overtaken by a later
    // one (a switch pressed twice, an answer landing) must not paint over it.
    let drawing = 0;

    // The load the eyes are waiting to send (toggleHidden()).
    let hideTimer = null;

    // Each load's own number, which the answer echoes as `seq`: an answer to a load overtaken by a later one (a
    // step taken while an eye's load was out, two quick Backs) is dropped, so it can neither put back the level
    // the reader left, nor end the dimming while the latest load is still out, nor spend its announcement.
    let loads = 0;

    // The landed topic's highlight, ending after HIGHLIGHT_MS (land()).
    let highlightTimer = null;

    // The opened group's search the server is about to be asked (searchGroup()), and the count said last.
    const searchTimers = new Map();
    let countTimer = null;

    // The level the address asked for as the card started.
    const arrival = viewFromAddress(typeof window !== 'undefined' ? window.location?.search : '');

    return {
        strings,

        /**
         * The level the card asks for: the band (`intent`) and the topic opened in it, as the address names
         * them. The answer says which it drew (answer.view), which can fill in a band a link left out.
         */
        view: arrival,

        /** The rows the reader took off the chart on the level on screen, by key. A new level starts with none. */
        hidden: [],

        /** The page of the table a band's topics are on, from 0 (TABLE_ROWS a page). */
        page: 0,

        /** A link's topic is still to be brought on screen, which waits for its answer. */
        isLandingDue: arrival.topic !== null,

        /** The reader stepped to another level: focus goes to its heading once it answers. */
        isLevelFocusDue: false,

        /** The group a link landed on, highlighted for a moment (land()), or null. */
        highlightedTopic: null,

        /** loading | ready | refused | rate_limited | error | no_query_rows | no_search_console */
        state: LOADING,

        /** The last answer that carried the card's figures or its state. */
        answer: null,

        /** A load is out: the figures on screen stay until it answers, dimmed. */
        isBusy: false,

        measure: CLICKS,

        /** Group key -> {state, rows, count, shown, note}, for the groups asked for. */
        groups: {},

        /** Query -> {state, rows, more, notes}, for the queries asked for. */
        pages: {},

        openQueries: [],

        /** Group key -> what its search box holds, for the opened group searched (Ola's QA, 9 Oct). */
        groupSearch: {},

        /**
         * Group key -> the server's answer to its search, for a group not all of whose searches the card
         * holds: {search, state, rows, count}, `count` the matches'.
         */
        groupMatches: {},

        /**
         * Whether the reader asked to read the AI's Unsafe searches (D47): one Show queries for the whole list (D204; Reine,
         * 23 Oct: never a button on each search), kept while the page is open.
         */
        revealedUnsafe: false,

        /** What the box over one band's topics holds, as typed (D173): the table lists the topics whose name holds it. */
        topicSearch: '',

        /** Each sorting table's order, { key, direction }: the server's to start, then the last header pressed. */
        sorts: { [SORT_TOPICS]: SERVER_SORT, [SORT_SEARCHES]: SERVER_SORT },

        announcement: '',

        // Back and Forward between this card's levels (go()), removed with the component.
        onPop: null,

        /**
         * Alpine's own start: the header's Clicks / Page views switch sets `measure` (the segmented
         * part's Alpine form, S47), and the chart follows it. Back and Forward reopen the level their
         * address names.
         */
        init() {
            this.$watch('measure', () => this.draw());
            this.onPop = () => this.popped();
            window.addEventListener('popstate', this.onPop);
        },

        // A destroyed card does nothing more. Every card on a page paints the one chart host, so a draw still
        // waiting on the chart modules would otherwise land on the card that replaced this one and put a stale
        // stack on it; bumping the draw number overtakes it the way a later draw would. The timers go too, so no
        // eye's load, highlight or search ask fires from a card that is gone.
        destroy() {
            window.removeEventListener('popstate', this.onPop);
            // A draw still on its way (it awaits the chart modules) must not post once the card is gone: its chart would
            // land on the host of whichever card took this one's place, under the same host id.
            drawing += 1;
            clearTimeout(hideTimer);
            hideTimer = null;
            clearTimeout(highlightTimer);
            highlightTimer = null;
            clearTimeout(countTimer);
            countTimer = null;
            searchTimers.forEach((timer) => clearTimeout(timer));
            searchTimers.clear();
        },

        /** A page load that names no single card is out: see receive(). */
        pending() {
            this.isBusy = true;
        },

        /**
         * The card's answer. A load that moved nothing it shows answers kept, and so does not touch the
         * figures; a refused period keeps them as the page keeps its own. Otherwise the answer replaces
         * what is on screen. A new question (another period, search type or filter) drops every opened
         * search's answer, which answered the old one; new topic rules under the same question (a topic
         * edit, a regroup) ask the opened group again.
         */
        receive(answer = {}) {
            if (typeof answer.seq === 'number' && answer.seq !== loads) return;

            // An eye's load still waits to go out (toggleHidden()), so the figures stay dimmed for it.
            this.isBusy = hideTimer !== null;

            if (answer.state === KEPT) return;

            if (answer.state === REFUSED && this.answer !== null) return;

            const isNewQuestion = answer.context !== this.answer?.context;
            const isRegrouped = !isNewQuestion && this.answer !== null && answer.rules !== this.answer.rules;
            const anchor = this.focusAnchor();

            if (isNewQuestion) {
                this.groups = {};
                this.pages = {};
                this.openQueries = [];
                this.forgetSearches();
            }

            this.answer = answer;
            this.state = answer.state;

            if (this.state === READY) {
                const drawn = { intent: answer.view?.intent ?? null, topic: answer.view?.topic ?? null };

                // The level the answer drew can be fuller than the one asked for (a link's topic, in the band its
                // search intent puts it in) or emptier (a topic deleted since): the address follows it in place. An
                // answer to an older load never gets here (`seq`), so a mismatch is never the level the reader left.
                if (!isSameView(drawn, this.view)) {
                    this.view = drawn;
                    this.writeAddress(drawn, false);
                }

                // The rows the server kept off the chart, unless the reader has pressed another eye since, whose
                // load is still to go: then the browser's list is the newer one.
                if (hideTimer === null) this.hidden = answer.view?.hidden ?? [];
                this.page = Math.min(this.page, Math.max(0, this.pageCount() - 1));

                const group = this.group();
                const key = this.groupKey(group);

                // New topic rules (a topic edit, a regroup) move what any group holds: every list read under the
                // old rules goes, but the one on screen, which is asked again in place below.
                if (isRegrouped) this.groups = key !== null && key in this.groups ? { [key]: this.groups[key] } : {};

                const held = key === null ? undefined : this.groups[key];

                // The searches level reads its group's searches once, and again in place after a topic edit.
                if (group === null) {
                    this.openQueries = [];
                } else if (group.isOpenable && (held === undefined || isRegrouped)) {
                    this.askGroup(group, held?.state === READY);
                }

                // The reader's pick of page views cannot draw a stack this level does not hold (Google Analytics
                // not connected, or a level that holds searches, not pages).
                if (!this.chartFor(this.measure)) this.measure = CLICKS;

                if (this.isLandingDue || this.isLevelFocusDue) this.land();
            }

            this.restoreFocus(anchor);

            if (this.state === 'error') this.announcement = this.strings.failed;
            if (this.state === 'rate_limited') this.announcement = this.strings.rateLimited;

            this.draw();
        },

        /** The card's Retry: focus parks on its heading, since the button is about to go. */
        retry() {
            this.$root.closest('[data-question-card]')?.querySelector('[data-card-heading]')?.focus();
            this.isBusy = true;
            this.state = LOADING;
            this.draw();
            this.call('loadTopics', null, this.request()).catch(() => {
                if (this.state !== LOADING) return;

                this.isBusy = false;
                this.state = 'error';
                this.announcement = this.strings.failed;
                this.draw();
            });
        },

        /**
         * The card's own load, with the level it is on and the rows taken off its chart: on init with no keys,
         * and on each page load with the keys it claimed (Analytics::loadTopics()).
         */
        load(keys = null) {
            this.call('loadTopics', keys, this.request()).catch(() => {
                this.isBusy = false;
            });
        },

        /** What every load asks for, numbered so its answer can be told from an older load's (`loads`). */
        request() {
            clearTimeout(hideTimer);
            hideTimer = null;

            return { intent: this.view.intent, topic: this.view.topic, hidden: [...this.hidden], seq: ++loads };
        },

        // ------------------------------------------------------------ levels (D148)

        level() {
            return this.state === READY ? (this.answer?.view?.level ?? LEVEL_BANDS) : LEVEL_BANDS;
        },

        /** The breadcrumb's places, from the first level to the one on screen. */
        trail() {
            return this.state === READY ? (this.answer?.view?.trail ?? []) : [];
        },

        /**
         * Another level, as a new history entry (Back undoes it) or, for one the browser already moved to (Back,
         * Forward), in place. A new level starts with every row on the chart and the table on its first page.
         * The figures dim until it answers, and focus goes to the level's own heading then (land()).
         */
        go(view, { push = true } = {}) {
            const next = { intent: view.intent ?? null, topic: view.topic ?? null };

            if (isSameView(next, this.view)) return;

            this.view = next;
            this.hidden = [];
            this.page = 0;
            this.openQueries = [];
            // Leaving a level closes its searches' box and its topics' box, as closing a row did: back on it, the
            // list starts whole.
            this.forgetSearches();
            this.topicSearch = '';
            this.isBusy = true;
            this.isLevelFocusDue = true;

            if (push) this.writeAddress(next, true);

            this.load();
        },

        /** A row's name pressed: a band opens to its topics, a topic to its searches. */
        open(row) {
            if (!row?.isOpenable) return;

            if (this.level() === LEVEL_BANDS) {
                this.go({ intent: row.key, topic: null });

                return;
            }

            if (this.level() === LEVEL_TOPICS) this.go({ intent: this.view.intent, topic: row.group });
        },

        /** A place in the breadcrumb pressed. */
        up(place) {
            this.go({ intent: place.intent ?? null, topic: place.topic ?? null });
        },

        /**
         * The level above the one on screen, which the card's Back button returns to, or null on the first level.
         * Within the card, never the browser's history: a reader who came by a link to a topic goes up to its band,
         * not back to the activity the link was in.
         */
        parent() {
            const trail = this.trail();

            return trail.length > 1 ? trail[trail.length - 2] : null;
        },

        /** The card's Back button: one level up, as a new history entry, as the breadcrumb's parent place goes. */
        back() {
            const place = this.parent();

            if (place !== null) this.up(place);
        },

        /**
         * Back or Forward to another of this card's levels on Stats: the level its address names, in place. An
         * entry for the activities is the views script's (insights-views.js), and changes nothing here.
         */
        popped() {
            const url = currentAddress();

            if (url === null || url.searchParams.get(VIEW_PARAM) !== STATS_VIEW) return;

            this.go(viewFromAddress(url.search), { push: false });
        },

        /**
         * The level in the address: ?intent= and ?topic=, as a new history entry for a step the reader took, or
         * in place for a level the answer filled in. Every other parameter (the view, the Period) stays.
         */
        writeAddress(view, isStep) {
            const url = currentAddress();

            // Only Stats' own address: an answer that lands after the reader left Stats must not write Stats'
            // level into the activities' address.
            if (url === null || url.searchParams.get(VIEW_PARAM) !== STATS_VIEW) return;

            if (view.intent === null) url.searchParams.delete(INTENT_PARAM);
            else url.searchParams.set(INTENT_PARAM, view.intent);

            if (view.topic === null) url.searchParams.delete(TOPIC_PARAM);
            else url.searchParams.set(TOPIC_PARAM, String(view.topic));

            if (url.href === window.location.href) return;

            if (isStep) window.history.pushState(window.history.state, '', url);
            else window.history.replaceState(window.history.state, '', url);
        },

        /**
         * A link opened a topic: insights-focus-topic, which the page's views script sends each time a link opens
         * Stats in place, so a card already showing figures opens it rather than only once per page. The link has
         * written ?topic= already; the band comes back with the answer.
         */
        focus(topic) {
            const group = topicId(topic);

            if (group === null) return;

            const named = viewFromAddress(currentAddress()?.search ?? '');

            this.isLandingDue = true;
            this.go({ intent: named.topic === group ? named.intent : null, topic: group }, { push: false });
            this.isLevelFocusDue = false;

            // The topic is on screen already (Forward to it): only brought into view. The same link twice asks
            // again, since the views script clears ?intent= as the link opens Stats, and lands the same.
            if (!this.isBusy && this.state === READY) this.land();
        },

        /**
         * A link's topic opened, said, highlighted for a moment, and brought on screen once the card holds still,
         * with focus on the level's heading when the reader has not taken it elsewhere meanwhile. Also where focus
         * goes after the reader steps to another level, so it never stays on a row that is gone.
         */
        land() {
            // A link to a topic the answer does not have (deleted, another site's) lands nowhere: the bands are
            // said as any level is, and nothing scrolls or lights.
            const isLink = this.isLandingDue && this.view.topic !== null;
            const isStep = this.isLevelFocusDue === true || this.isLandingDue;

            this.isLandingDue = false;
            this.isLevelFocusDue = false;

            if (this.state !== READY) return;

            const name = this.trail().at(-1)?.name ?? '';

            this.announcement = this.fill(isLink ? this.strings.topicLanded : this.strings.levelShown, { ':name': name });

            this.$nextTick(() => {
                const heading = this.$root?.querySelector?.('[data-topics-level]') ?? null;

                if (!isLink) {
                    if (isStep) heading?.focus({ preventScroll: true });

                    return;
                }

                const card = this.$root?.closest?.('[data-question-card]') ?? null;

                // The topic's own row, under the chart, when it has rendered; the card's top else.
                const row = this.$root?.querySelector?.('[data-topics-group-row]') ?? card;

                whenSettled(card, () => {
                    scrollToLanding(card, row, window.innerHeight ?? 0, isMotionReduced());
                    this.flash(this.view.topic);

                    if (isFocusFree()) heading?.focus({ preventScroll: true });
                }, () => this.flash(this.view.topic));
            });
        },

        /** A link's topic highlighted for HIGHLIGHT_MS, as the activity table's row a link brings forward is. */
        flash(group) {
            clearTimeout(highlightTimer);
            this.highlightedTopic = group;
            highlightTimer = setTimeout(() => {
                highlightTimer = null;
                this.highlightedTopic = null;
            }, HIGHLIGHT_MS);
        },

        isHighlighted(row) {
            return this.highlightedTopic !== null && row?.group === this.highlightedTopic;
        },

        /** Leaving Stats ends a link's moment; the level stays in the address for Back. */
        endFocus() {
            clearTimeout(highlightTimer);
            this.highlightedTopic = null;
            this.isLandingDue = false;
            this.isLevelFocusDue = false;
        },

        // ------------------------------------------------------------ the rows taken off the chart

        isHidden(row) {
            return this.hidden.includes(row.key);
        },

        /**
         * A row's eye: the row leaves the chart, or comes back, and the server composes the stack without it (so
         * one summed into Other topics leaves that sum too). Said in the live region, since the redraw is the only
         * other sign of it. Presses a moment apart go as one load; the figures dim at once.
         */
        toggleHidden(row) {
            const wasHidden = this.isHidden(row);

            this.hidden = wasHidden ? this.hidden.filter((key) => key !== row.key) : [...this.hidden, row.key];
            this.announcement = this.fill(wasHidden ? this.strings.rowShown : this.strings.rowHidden, { ':name': row.name });
            this.isBusy = true;
            clearTimeout(hideTimer);
            hideTimer = setTimeout(() => {
                hideTimer = null;
                this.load();
            }, HIDE_DEBOUNCE_MS);
        },

        /** Every row back on the chart. */
        showAll() {
            if (this.hidden.length === 0) return;

            this.hidden = [];
            this.announcement = this.strings.allShown;
            this.isBusy = true;
            this.load();
        },

        /** The sentence under the chart while rows are off it: which, and that the chart is not the whole. */
        hiddenNote() {
            const names = this.allRows().filter((row) => this.isHidden(row)).map((row) => row.name);

            return names.length === 0 ? '' : this.fill(this.strings.hiddenNote, { ':name': names.join(', ') });
        },

        // ------------------------------------------------------------ the chart

        hasPageViews() {
            return this.state === READY && Boolean(this.chartFor(PAGE_VIEWS));
        },

        chartFor(measure) {
            return this.answer?.charts?.[measure] ?? null;
        },

        /** Whether the chart has a stack to draw now; the card's other states say why in words. */
        hasStack() {
            return this.state === READY && Boolean(this.chartFor(this.measure));
        },

        setMeasure(measure) {
            if (measure === this.measure) return;

            this.measure = measure;
            this.draw();
        },

        async draw() {
            const current = ++drawing;

            if (this.state === LOADING) {
                this.toHost({ status: 'loading' });

                return;
            }

            const stack = this.state === READY ? this.chartFor(this.measure) : null;

            if (!stack) {
                this.toHost({ status: 'empty', emptyMessage: this.strings.emptyChart });

                return;
            }

            const [chart, options, { tooltipPlacement }] = await Promise.all([
                import('../charts/site-analytics-chart-contract'),
                import('../charts/site-analytics-options'),
                import('../charts/tooltip-placement'),
            ]);

            if (current !== drawing) return;

            const model = stackModel(stack);
            const { contract, drawn } = chart.lineChart(model, { formats, stack: true, hidden: [], fill: STACK_FILL, lineWidth: STACK_LINE_WIDTH, markers: false, focus: null });

            // Nothing drawn while rows are off the chart means every one is, and the empty box says so in the
            // page's own words for it, rather than that the period had nothing to draw.
            if (!contract) {
                this.toHost({ status: 'empty', emptyMessage: this.hidden.length > 0 ? this.strings.allHidden : this.strings.emptyChart });

                return;
            }

            // Made on the first hover, when ECharts asks: tooltipPlacement() reads the document.
            let placed = null;
            const placement = (...args) => (placed ??= tooltipPlacement(hostId))(...args);

            this.toHost({
                title: this.strings.chart?.[this.measure] ?? '',
                contract,
                // The v3 spec's dark tooltip: the date and the total, then each band from the top down. The server
                // words the total for a chart with rows off it ("48 of 63"), so it is always the truth.
                tooltip: options.stackTooltip(drawn, model.dates, { noDataPeriod: this.strings.noDataPeriod }, { total: model.total, placement }),
                periods: chart.periodLabels(model),
            });
        },

        toHost(detail) {
            const key = JSON.stringify({ ...detail, tooltip: undefined });

            if (key === sent) return;

            sent = key;
            window.dispatchEvent(new CustomEvent(HOST_EVENT, { detail: { id: hostId, ...detail } }));
        },

        /** The sentence under the chart for the level on screen. */
        chartNote() {
            if (!this.hasStack()) return '';

            const band = this.answer?.band;

            if (this.level() === LEVEL_TOPICS && band) return this.fill(this.strings.noteTopics, { ':name': band.name, ':count': band.count });
            if (this.level() === LEVEL_SEARCHES) return this.fill(this.strings.noteSearches, { ':name': this.trail().at(-1)?.name ?? '' });

            return this.measure === PAGE_VIEWS ? this.strings.noteBandsPageViews : this.strings.noteBands;
        },

        // ------------------------------------------------------------ the table

        /** Every row the answer holds at this level. */
        allRows() {
            return this.state === READY ? (this.answer?.rows ?? []) : [];
        },

        /**
         * The rows the table shows: every band, or one page of a band's topics. The searches level shows its one
         * row above its searches instead (group()).
         */
        rows() {
            if (this.level() === LEVEL_SEARCHES) return [];

            if (this.level() !== LEVEL_TOPICS) return this.allRows();

            return this.topicMatches().slice(this.page * TABLE_ROWS, (this.page + 1) * TABLE_ROWS);
        },

        /** One band's topics the box over them lets through, in the table's order: every one while it is empty. */
        topicMatches() {
            const needle = this.topicSearch.trim().toLowerCase();
            const rows = needle === '' ? this.allRows() : this.allRows().filter((row) => String(row.name ?? '').toLowerCase().includes(needle));

            return this.sorted(rows, SORT_TOPICS);
        },

        /**
         * A key in the box over one band's topics: the table lists the topics whose name holds the text, whatever its
         * case, from its first page, and how many is said once the reader stops typing. Every topic is held, so it
         * asks nothing.
         */
        searchTopics(text) {
            this.topicSearch = String(text ?? '');
            this.page = 0;
            clearTimeout(countTimer);
            countTimer = setTimeout(() => {
                const said = this.topicsCount();

                if (said !== '') this.say(said);
            }, COUNT_ANNOUNCE_MS);
        },

        /** "Showing 3 of 14 topics" while the box holds text, or what is said when no topic matches; '' while empty. */
        topicsCount() {
            const search = this.topicSearch.trim();

            if (this.level() !== LEVEL_TOPICS || search === '') return '';

            const shown = this.topicMatches().length;

            return shown === 0
                ? this.fill(this.strings.topicsNoMatch, { ':name': this.answer?.band?.name ?? '', ':query': search })
                : this.fill(this.strings.topicsMatching, { ':shown': this.countText(shown), ':count': this.countText(this.allRows().length) });
        },

        /** The searches level's one row: the topic, Other queries or Branded queries drawn alone. */
        groupRow() {
            return this.level() === LEVEL_SEARCHES ? (this.allRows()[0] ?? null) : null;
        },

        pageCount() {
            return this.level() === LEVEL_TOPICS ? Math.ceil(this.topicMatches().length / TABLE_ROWS) : 1;
        },

        hasPages() {
            return this.pageCount() > 1;
        },

        /** "11–20 of 34", in the site's notation. */
        pageText() {
            const count = this.level() === LEVEL_TOPICS ? this.topicMatches().length : this.allRows().length;
            const from = this.page * TABLE_ROWS + 1;
            const to = Math.min(count, (this.page + 1) * TABLE_ROWS);

            return this.fill(this.strings.pageOf, { ':shown': `${this.countText(from)}–${this.countText(to)}`, ':count': this.countText(count) });
        },

        /** Another page of a band's topics; focus stays on the button pressed, or moves to its twin at an end. */
        turnPage(step) {
            const next = Math.min(Math.max(0, this.page + step), this.pageCount() - 1);

            if (next === this.page) return;

            this.page = next;
            this.announcement = this.pageText();
            this.$nextTick(() => {
                const buttons = this.$root?.querySelectorAll?.('[data-topics-page]') ?? [];
                const pressed = [...buttons].find((button) => Number(button.dataset.topicsPage) === step);

                (pressed && !pressed.disabled ? pressed : [...buttons].find((button) => !button.disabled))?.focus();
            });
        },

        /** What one band's topics add up to, under its table: "All 14 add up to Bottom of funnel: 1,234 clicks". */
        bandTotal() {
            const band = this.level() === LEVEL_TOPICS ? this.answer?.band : null;

            return band ? this.fill(this.strings.bandTotal, { ':count': this.countText(band.count), ':name': band.name, ':shown': band.clicks ?? '' }) : '';
        },

        notes() {
            return this.state === READY ? (this.answer?.notes ?? []) : [];
        },

        /** Why the card has no figures, in the answer's own words, or the card's for its own states. */
        stateMessage() {
            if (this.state === 'no_query_rows' || this.state === 'no_search_console') return this.answer?.note ?? '';
            if (this.state === 'error') return this.strings.failed;
            if (this.state === 'rate_limited') return this.strings.rateLimited;
            if (this.state === REFUSED) return this.strings.nothingYet;

            return '';
        },

        canRetry() {
            return this.state === 'error' || this.state === 'rate_limited';
        },

        /** A row's dot: the colour of the band it is drawn in, or summed into (Other topics). */
        dotColor(row) {
            return bandColor(row?.color);
        },

        rowId(row) {
            return `${id}-row-${row.key}`;
        },

        // ------------------------------------------------------------ the searches level (D18)

        /** The group the searches level reads its searches from (TopicsCardBuilder::searchesLevel()), or null. */
        group() {
            return this.level() === LEVEL_SEARCHES ? (this.answer?.group ?? null) : null;
        },

        /**
         * A group's key: its id, and which of its searches it lists, so a Bottom of funnel topic opened under Bottom
         * and under Middle of funnel are two lists.
         */
        groupKey(group) {
            if (!group) return null;

            const share = group.isBuying === true ? 'buying' : group.isBuying === false ? 'rest' : 'all';

            return `${group.id}:${share}`;
        },

        isGroupOpen(group) {
            return Boolean(group?.isOpenable) && this.groupKey(group) === this.groupKey(this.group());
        },

        /**
         * Ask for the group's searches. `isRefresh` asks again for a group already on screen: its rows stay until
         * the new ones land, so the reader is not left on a loading row, nor focus on a row that is taken away
         * before there is anything to put in its place.
         */
        askGroup(group, isRefresh = false) {
            const key = this.groupKey(group);
            const held = this.groups[key];

            this.groups = {
                ...this.groups,
                [key]: isRefresh && held?.state === READY ? { ...held, isRefreshing: true } : { state: LOADING, rows: [] },
            };

            if (!isRefresh) this.announcement = this.fill(this.strings.loadingQueries, { ':name': group.name });

            this.call('openTopicGroup', group.id, null, group.isBuying ?? null).catch(() => this.failGroup(key));
        },

        /** One group's searches, dropped when they answered a question the card has moved on from. */
        receiveQueries(answer = {}) {
            const key = this.groupKey({ id: answer.group, isBuying: answer.buying ?? null });

            // The answer to a search box (searchGroup()), which names the search it answers.
            if (typeof answer.search === 'string' && answer.search !== '') {
                this.receiveMatches(key, answer);

                return;
            }

            if (!(key in this.groups) || this.isStale(answer)) return;

            const anchor = this.focusAnchor();

            this.groups = {
                ...this.groups,
                [key]: {
                    state: answer.state,
                    rows: answer.rows ?? [],
                    count: answer.count ?? 0,
                    shown: answer.shown ?? null,
                    shownSorted: answer.shownSorted ?? null,
                    note: answer.note ?? null,
                },
            };

            if (key === this.groupKey(this.group())) {
                this.announcement = answer.state === READY
                    ? (answer.announcement ?? '')
                    : (answer.state === 'rate_limited' ? this.strings.rateLimited : this.strings.rowFailed);
            }

            this.restoreFocus(anchor);

            // A group read again (a topic edit, a regroup) while its box holds a search: the search is
            // answered again against what the group holds now.
            const group = this.group();

            if (group && this.groupKey(group) === key && this.searchOf(group) !== '') this.searchGroup(group, this.searchTermOf(group));
        },

        failGroup(key) {
            if (this.groups[key]?.state !== LOADING && !this.groups[key]?.isRefreshing) return;

            this.groups = { ...this.groups, [key]: { state: 'error', rows: [] } };
            this.announcement = this.strings.rowFailed;
        },

        // ------------------------------------------------------------ an opened topic's search (Ola's QA, 9 Oct)

        /** Whether the opened group offers its search box: it holds a search (every list, however short, since D173). */
        hasSearchBox(group) {
            const held = this.groups[this.groupKey(group)];

            return held?.state === READY && (held.count ?? 0) > 0;
        },

        /** What the group's search box holds, as typed. */
        searchTermOf(group) {
            return this.groupSearch[this.groupKey(group)] ?? '';
        },

        /** What the group is narrowed by: the box's text, trimmed, or '' for no search. */
        searchOf(group) {
            return this.searchTermOf(group).trim();
        },

        /** Whether the card holds every search of the group, so the box is answered here and asks nothing. */
        isHeldWhole(held) {
            return (held?.rows?.length ?? 0) >= (held?.count ?? 0);
        },

        /**
         * A key in the group's search box: answered at once from the searches the card holds, when it holds
         * all of them; otherwise asked of the server a moment after the last key (Analytics::
         * openTopicGroup()'s search), since one read lists the first TopicSql::MAX_QUERIES only. The list
         * goes back to its top, so a narrowed list is never shown scrolled past its end.
         */
        searchGroup(group, text) {
            const key = this.groupKey(group);
            const held = this.groups[key];
            const search = String(text ?? '').trim();

            this.groupSearch = { ...this.groupSearch, [key]: String(text ?? '') };
            clearTimeout(searchTimers.get(key));
            this.$nextTick(() => document.getElementById?.(this.windowId(group))?.scrollTo?.({ top: 0 }));

            if (search === '' || held?.state !== READY || this.isHeldWhole(held)) {
                this.sayCount(group);

                return;
            }

            this.groupMatches = { ...this.groupMatches, [key]: { search, state: LOADING, rows: [], count: 0 } };
            searchTimers.set(key, setTimeout(() => this.askMatches(group, search), SEARCH_ASK_MS));
        },

        /** The group's search asked of the server, in the searches' order on screen (D173), which the answer echoes. */
        askMatches(group, search) {
            const key = this.groupKey(group);
            const order = this.serverOrder();
            const token = orderToken(order);

            this.groupMatches = { ...this.groupMatches, [key]: { ...this.groupMatches[key], order: token } };

            this.call('openTopicGroup', group.id, search, group.isBuying ?? null, ...(order === null ? [] : [order.column, order.direction])).catch(() => {
                const held = this.groupMatches[key];

                if (held?.search !== search || held.order !== token || (held.state !== LOADING && !held.isReordering)) return;

                this.groupMatches = { ...this.groupMatches, [key]: { search, order: token, state: 'error', rows: [], count: 0 } };
                this.announcement = this.strings.rowFailed;
            });
        },

        /**
         * The server's answer to the group's search, kept only while the box still holds that search and the
         * searches are still in the order it was asked in. An answer to a new order alone says nothing: the
         * header's press has said the order, and the count has not moved.
         */
        receiveMatches(key, answer) {
            const held = this.groupMatches[key];

            if (!held || held.search !== answer.search || held.order !== orderToken(answer.order ?? null) || this.isStale(answer)) return;

            this.groupMatches = { ...this.groupMatches, [key]: { search: answer.search, order: held.order, state: answer.state, rows: answer.rows ?? [], count: answer.count ?? 0 } };

            const group = this.group();

            if (answer.state !== READY) {
                this.announcement = answer.state === 'rate_limited' ? this.strings.rateLimited : this.strings.rowFailed;
            } else if (group && this.groupKey(group) === key && !held.isReordering) {
                this.sayCount(group);
            }
        },

        /**
         * The searches the group's list shows: every one it holds, or those its search box matches, whatever
         * the case (the page's own Contains), from the searches held when the card holds the group whole,
         * else from the server's answer. `status` stands in their place while that answer is out or failed,
         * and `matched` is how many the server found, which can be more than it listed.
         */
        listedQueries(group, held) {
            const search = this.searchOf(group);

            if (search === '') return { rows: held.rows };

            if (this.isHeldWhole(held)) {
                const needle = search.toLowerCase();

                return { rows: held.rows.filter((query) => String(query.query).toLowerCase().includes(needle)) };
            }

            const found = this.groupMatches[this.groupKey(group)];

            if (!found || found.search !== search || found.state === LOADING) return { rows: [], status: LOADING };
            if (found.state !== READY) return { rows: [], status: 'failed' };

            return { rows: found.rows, matched: found.count };
        },

        /** "Showing 3 of 143 searches" while the box holds a search, and '' while it does not or its answer is out. */
        searchCount(group) {
            const held = this.groups[this.groupKey(group)];

            if (held?.state !== READY || this.searchOf(group) === '') return '';

            const listed = this.listedQueries(group, held);

            if (listed.status !== undefined) return '';

            return this.fill(this.strings.searchCount, { ':shown': this.countText(listed.rows.length), ':count': this.countText(held.count) });
        },

        /** The count, said in the card's polite region once the reader stops typing. */
        sayCount(group) {
            clearTimeout(countTimer);
            countTimer = setTimeout(() => {
                const text = this.searchCount(group);

                if (text !== '') this.say(text);
            }, COUNT_ANNOUNCE_MS);
        },

        /** A sentence said in the card's polite region even when it is the one said last, which a region does not repeat. */
        say(text) {
            this.announcement = '';
            this.$nextTick(() => {
                this.announcement = text;
            });
        },

        /** Every search box's text and answer dropped, under a new question. */
        forgetSearches() {
            searchTimers.forEach((timer) => clearTimeout(timer));
            searchTimers.clear();
            this.groupSearch = {};
            this.groupMatches = {};
        },

        /** Whether the group's list holds more searches than it shows, so it scrolls and is a named, focusable region. */
        windowScrolls(group) {
            return this.childRows(group).filter((item) => item.type === 'query').length > SEARCH_WINDOW_ROWS;
        },

        windowId(group) {
            return `${id}-${group?.key ?? 'group'}-window`;
        },

        searchId(group) {
            return `${id}-${group?.key ?? 'group'}-search`;
        },

        /** A whole count in the site's notation, its thousands separated as the server writes its figures. */
        countText(value) {
            const thousands = formats?.count?.locale?.thousands ?? ',';

            return String(Math.trunc(Number(value) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        },

        // ------------------------------------------------------------ the rows under a search

        isQueryOpen(query) {
            return this.openQueries.includes(query);
        },

        toggleQuery(item) {
            if (this.isQueryOpen(item.query)) {
                this.openQueries = this.openQueries.filter((open) => open !== item.query);

                return;
            }

            this.openQueries = [...this.openQueries, item.query];

            if (this.pages[item.query]?.state === READY) return;

            this.askPages(item);
        },

        askPages(item) {
            this.pages = { ...this.pages, [item.query]: { state: LOADING, rows: [] } };
            this.announcement = this.fill(this.strings.loadingPages, { ':query': this.queryName(item) });
            this.call('openTopicQuery', item.query, item.impressionsValue).catch(() => this.failPages(item.query));
        },

        // ------------------------------------------------------------ Unsafe searches (D47, S29)

        /**
         * Whether a search row's text is masked: a search of the AI's Unsafe searches topic the reader
         * has not asked to read. Its row carries the topic's mark (TopicsCardBuilder), as the activity
         * table's rows carry theirs.
         */
        isMasked(item) {
            return item.isUnsafe === true && !this.revealedUnsafe;
        },

        /**
         * A search's name wherever the card shows or says it: its text, or the activity table's masked
         * word until Show, so neither the row nor a screen reader quotes an unsafe search unasked.
         */
        queryName(item) {
            return this.isMasked(item) ? (this.strings.unsafe ?? '') : item.query;
        },

        /**
         * Show queries and Hide queries over the Unsafe searches' list (D204): every one of its searches at once. Focus stays
         * on the button, whose words say what it does next.
         */
        toggleReveal() {
            this.revealedUnsafe = !this.revealedUnsafe;
        },

        receivePages(answer = {}) {
            const query = answer.query;

            if (typeof query !== 'string' || !(query in this.pages) || this.isStale(answer)) return;

            this.pages = {
                ...this.pages,
                [query]: {
                    state: answer.state,
                    rows: answer.rows ?? [],
                    more: answer.more ?? null,
                    notes: answer.notes ?? [],
                },
            };

            this.announcement = answer.state === READY
                ? (answer.announcement ?? '')
                : (answer.state === 'rate_limited' ? this.strings.rateLimited : this.strings.rowFailed);
        },

        failPages(query) {
            if (this.pages[query]?.state !== LOADING) return;

            this.pages = { ...this.pages, [query]: { state: 'error', rows: [] } };
            this.announcement = this.strings.rowFailed;
        },

        /**
         * An opened row's answer for another question than the one on screen, or a group's queries
         * read under topic rules the card has since moved on from. A row that failed to read its
         * question at all answers with none, and is shown its failure rather than dropped, so it
         * never spins.
         */
        isStale(answer) {
            const differs = (field) => Boolean(answer[field]) && Boolean(this.answer?.[field]) && answer[field] !== this.answer[field];

            return differs('context') || differs('rules');
        },

        /**
         * The rows of the opened group's list, as one list: its Hidden queries row (D207), the searches it
         * shows (listedQueries(): every one, or those its search box matches), each followed by its pages once
         * opened, and what is said in place of searches. Each item carries the id aria-controls names and x-for
         * keys it by, and its level: 1 a search, 2 a search's page. What is said about the list as a whole sits
         * under it (groupNotes()), outside the part that scrolls.
         */
        childRows(group) {
            if (!this.isGroupOpen(group)) return [];

            const base = `${id}-${group.key}`;
            const held = this.groups[this.groupKey(group)];

            if (!held || held.state === LOADING) {
                return [{ id: `${base}-status`, type: 'status', level: 1, text: this.fill(this.strings.loadingQueries, { ':name': group.name }) }];
            }

            if (held.state !== READY) {
                return [{
                    id: `${base}-failed`,
                    type: 'failed',
                    level: 1,
                    text: held.state === 'rate_limited' ? this.strings.rateLimited : this.strings.rowFailed,
                    retry: () => this.askGroup(group),
                }];
            }

            const search = this.searchOf(group);
            const listed = this.listedQueries(group, held);

            if (listed.status === LOADING) {
                return [{ id: `${base}-searching`, type: 'status', level: 1, text: this.fill(this.strings.searching, { ':query': search }) }];
            }

            if (listed.status !== undefined) {
                return [{
                    id: `${base}-search-failed`,
                    type: 'failed',
                    level: 1,
                    text: this.strings.rowFailed,
                    retry: () => this.searchGroup(group, this.searchTermOf(group)),
                }];
            }

            // The Unsafe searches' one Show queries (D204), over the list it shows or hides whole.
            const queries = group.isUnsafe === true && listed.rows.length > 0 ? [{ id: `${base}-reveal`, type: 'reveal', level: 1, name: group.name }] : [];

            // Its hidden clicks as one row before its queries (D207), whatever their order, which opens their pages
            // (P90); left out while the box narrows the list to the queries matching it.
            if (group.hidden && search === '') {
                queries.push({ id: `${base}-hidden`, key: `${base}|hidden`, type: 'hidden', level: 1, clicks: group.hidden, topic: group.id });
            }

            this.sorted(listed.rows, SORT_SEARCHES).forEach((query, index) => {
                // A search the card holds keeps its place in the whole list as its id, so a search box
                // narrowing the list renames nothing its row and its pages are named by.
                const place = held.rows.indexOf(query);
                const queryId = place === -1 ? `${base}-m${index}` : `${base}-q${place}`;
                const item = { ...query, id: queryId, key: `${base}|q|${query.query}`, type: 'query', level: 1, isBranded: group.isBranded, isUnsafe: group.isUnsafe === true };

                queries.push(item);

                if (this.isQueryOpen(query.query)) queries.push(...this.pageRows(item, queryId, item.key));
            });

            if (listed.rows.length === 0) {
                queries.push({
                    id: `${base}-empty`,
                    type: 'status',
                    level: 1,
                    text: search === '' ? this.strings.noQueries : this.fill(this.strings.noMatches, { ':name': group.name, ':query': search }),
                });
            }

            return queries;
        },

        /**
         * What is said under the opened group's list, outside the part that scrolls, so it is read without
         * scrolling past every search: the group's note, then how many searches it lists of how many it
         * holds (S49), or while its box holds a search the server matched more of than it listed, so. Both
         * say "by clicks" only while the list runs by clicks (D173).
         */
        groupNotes(group) {
            const held = this.isGroupOpen(group) ? this.groups[this.groupKey(group)] : null;

            if (held?.state !== READY) return [];

            const base = `${id}-${group.key}`;
            const notes = held.note ? [{ id: `${base}-note`, text: held.note }] : [];

            const isByClicks = this.serverOrder() === null;

            if (this.searchOf(group) === '') {
                const shown = isByClicks ? held.shown : (held.shownSorted ?? null);

                if (shown) notes.push({ id: `${base}-shown`, text: shown });

                return notes;
            }

            const listed = this.listedQueries(group, held);

            if (listed.matched !== undefined && listed.matched > listed.rows.length) {
                notes.push({ id: `${base}-more`, text: this.fill(isByClicks ? this.strings.moreMatch : this.strings.moreMatchSorted, { ':count': this.countText(listed.matched), ':shown': this.countText(listed.rows.length) }) });
            }

            return notes;
        },

        pageRows(query, parentId, parentKey) {
            const held = this.pages[query.query];

            if (!held || held.state === LOADING) {
                return [{ id: `${parentId}-status`, type: 'status', level: 2, text: this.fill(this.strings.loadingPages, { ':query': this.queryName(query) }) }];
            }

            if (held.state !== READY) {
                return [{
                    id: `${parentId}-failed`,
                    type: 'failed',
                    level: 2,
                    text: held.state === 'rate_limited' ? this.strings.rateLimited : this.strings.rowFailed,
                    retry: () => this.askPages(query),
                }];
            }

            const items = held.rows.map((page, index) => ({ ...page, id: `${parentId}-p${index}`, key: `${parentKey}|p|${page.page}`, type: 'page', level: 2 }));

            if (held.rows.length === 0) items.push({ id: `${parentId}-empty`, type: 'status', level: 2, text: this.strings.noPages });
            if (held.more) items.push({ id: `${parentId}-more`, type: 'note', level: 2, text: held.more });

            (held.notes ?? []).forEach((note, index) => items.push({ id: `${parentId}-note${index}`, type: 'note', level: 2, text: note }));

            return items;
        },

        /** The ids a search's button controls: every row under it, once it is open. */
        queryControls(group, item) {
            if (!this.isQueryOpen(item.query)) return null;

            return this.childRows(group).filter((child) => child.level === 2 && child.id.startsWith(`${item.id}-`)).map((child) => child.id).join(' ') || null;
        },

        // ------------------------------------------------------------ the headers' order (D173)

        /** The sorting table on screen: one band's topics, or one topic's searches; null for the bands. */
        sortTable() {
            if (this.level() === LEVEL_TOPICS) return SORT_TOPICS;
            if (this.level() === LEVEL_SEARCHES) return SORT_SEARCHES;

            return null;
        },

        /** Whether a header sorts now: its table is on screen and fills its column. */
        isSortable(key, table = this.sortTable()) {
            return table !== null && table === this.sortTable() && SORT_COLUMNS[table]?.[key] !== undefined;
        },

        /** The header's aria-sort (<x-insights.sortable-header>): only the sorted column of the table on screen carries one. */
        ariaSortOf(key, table = this.sortTable()) {
            return this.isSortable(key, table) ? ariaSort(this.sorts[table], key) : null;
        },

        sortArrowOf(key, table = this.sortTable()) {
            return this.isSortable(key, table) ? sortArrow(this.sorts[table], key) : '';
        },

        /**
         * A header pressed: the table sorts by its column, largest first, a name A to Z and a position top of
         * Google first, or the other way when it is the column already sorted; the topics go back to their first
         * page and the searches' window to its top. The order is said once in the card's polite region, in the
         * activity table's words ("Sorted by Impressions, highest first."). Focus stays on the header.
         */
        sortBy(key, table = this.sortTable()) {
            if (!this.isSortable(key, table)) return;

            const kind = SORT_COLUMNS[table][key];
            const sort = nextSort(this.sorts[table], key, kind === 'number' ? DESCENDING : ASCENDING);

            this.sorts = { ...this.sorts, [table]: sort };

            if (table === SORT_TOPICS) this.page = 0;
            else this.reorderSearches();

            this.say(sortSentence(this.strings.sorted, this.strings.directions, this.strings.columns?.[table]?.[key] ?? key, sort.direction, kind === 'number' ? null : kind));
        },

        /** Rows in a table's order, from what each carries for the sorted column. */
        sorted(rows, table) {
            const sort = this.sorts[table];
            const kind = SORT_COLUMNS[table]?.[sort?.key];

            if (kind === undefined) return rows;

            return sortRows(rows, sort, (row) => {
                if (kind === 'text') return table === SORT_TOPICS ? (row.name ?? null) : (row.query ?? null);

                return row.sort?.[sort.key] ?? null;
            });
        },

        /** The searches' order as the server reads it (TopicSql::queryOrder()): null for its own, clicks most first. */
        serverOrder() {
            const sort = this.sorts[SORT_SEARCHES];

            return sort.key === SERVER_SORT.key && sort.direction === SERVER_SORT.direction ? null : { column: sort.key, direction: sort.direction };
        },

        /**
         * The searches' window back at its top in the new order. A search the browser cannot answer whole is
         * asked again in it, since the server's first matches in the old order are not the first in the new;
         * the matches on screen stay, sorted here, until the new ones land. One whose matches are all held is
         * sorted here and asks nothing.
         */
        reorderSearches() {
            const group = this.group();

            if (group === null) return;

            this.$nextTick(() => document.getElementById?.(this.windowId(group))?.scrollTo?.({ top: 0 }));

            const key = this.groupKey(group);
            const held = this.groups[key];
            const search = this.searchOf(group);

            if (search === '' || held?.state !== READY || this.isHeldWhole(held)) return;

            const found = this.groupMatches[key];
            const isShown = found?.search === search && found.state === READY;

            if (isShown && found.count <= found.rows.length) return;

            clearTimeout(searchTimers.get(key));
            searchTimers.delete(key);
            this.groupMatches = {
                ...this.groupMatches,
                [key]: isShown ? { ...found, isReordering: true } : { search, state: LOADING, rows: [], count: 0 },
            };
            this.askMatches(group, search);
        },

        // ------------------------------------------------------------ Draft an article

        /** Draft an article on one query, opened on the card's period and search type (D13). */
        draft(item) {
            const draft = this.answer?.draft;

            if (!draft || !item.isDraftable) return;

            window.dispatchEvent(new CustomEvent('open-draft-article', {
                detail: { query: item.query, searchType: draft.searchType, period: draft.period, start: draft.start, end: draft.end },
            }));
        },

        // ------------------------------------------------------------ focus across a redraw

        /**
         * The control in the card's table that has focus, and where it sits, before an answer redraws it;
         * null when focus is anywhere else. A redraw can re-sort the rows (by clicks), which blurs whatever
         * was focused inside them, or take a search's row away (a topic edit moved it).
         */
        focusAnchor() {
            const active = document.activeElement;

            if (!active || !this.$root.contains(active) || !active.closest('[data-topics-table]')) return null;

            return {
                element: active,
                query: active.closest('tr[data-topic-query]')?.dataset.topicQuery ?? null,
                eye: active.dataset?.topicsEye ?? null,
            };
        },

        /**
         * Focus put back once the redraw has landed: on the same control if it is still on the page, else on
         * the same row's eye, else on the same search's button.
         */
        restoreFocus(anchor) {
            if (!anchor) return;

            this.$nextTick(() => {
                if (document.activeElement === anchor.element) return;

                const byEye = anchor.eye === null ? null : this.$root.querySelector(`[data-topics-eye="${CSS.escape(anchor.eye)}"]`);
                const byQuery = anchor.query === null ? null : this.$root.querySelector(`tr[data-topic-query="${CSS.escape(anchor.query)}"] button[aria-expanded]`);

                (anchor.element.isConnected ? anchor.element : (byEye ?? byQuery))?.focus();
            });
        },

        // ------------------------------------------------------------ helpers

        /**
         * An opened row's search, or what is said in its place, rising 6px as it fades in (the v3 spec's
         * row opening, D74), once, as it is drawn. Under reduced motion it is simply there.
         */
        fadeUp(element) {
            if (prefersReducedMotion() || typeof element?.animate !== 'function') return;

            element.animate(
                [{ opacity: 0, transform: `translateY(${FADE_UP_PX}px)` }, { opacity: 1, transform: 'translateY(0)' }],
                { duration: motion.duration.show, easing: motion.ease },
            );
        },

        /** A row under a topic that is a sentence rather than a query or a page: it spans the table. */
        isMessage(item) {
            return item.type === 'status' || item.type === 'failed' || item.type === 'note' || item.type === 'reveal';
        },

        /** A query's or a page's figure for one column, formatted by the server, or null for none. */
        figureOf(item, field) {
            return item[field] ?? null;
        },

        /** Good news, bad news or neither, by the measure's own direction (DeltaCalculator's tone). */
        changeTone(change) {
            if (!change) return 'text-gray-700';

            return change.tone === 'good' ? 'text-green-800' : change.tone === 'bad' ? 'text-red-800' : 'text-gray-700';
        },

        changeArrow(change) {
            if (!change) return '';

            return change.direction === 'up' ? '▲' : change.direction === 'down' ? '▼' : '–';
        },

        changeDirection(change) {
            if (!change) return '';

            return change.direction === 'up' ? this.strings.changeUp : change.direction === 'down' ? this.strings.changeDown : this.strings.changeFlat;
        },

        /**
         * A call to the page's component. Outside Livewire (a preview, a static embed) `$wire` throws
         * on first touch rather than answering undefined, so the call itself is the test, as
         * site-analytics-report.js's sar_reload() finds.
         */
        call(method, ...args) {
            try {
                return Promise.resolve(this.$wire[method](...args));
            } catch (error) {
                console.debug(`The topic card found no Livewire component to call ${method} on:`, error);

                return Promise.reject(error);
            }
        },

        /** :placeholders replaced in one pass, with a function, as sar_fill() does and for its reasons. */
        fill(template, values) {
            return String(template ?? '').replace(/:name|:query|:count|:max|:shown/g, (token) => String(values[token] ?? ''));
        },
    };
};
